// Local diagnostic data only. Nothing here writes pupil evidence to the server.
export function recordingKey(ownerId, lessonId) {
  if (![ownerId, lessonId].every(v => typeof v === "string" && v.length > 0)) throw Error("Choose a signed-in lesson first.");
  return JSON.stringify([ownerId, lessonId]);
}

export function createMotionRecordingStore({ indexedDB, IDBKeyRange }) {
  let connection;
  function database() {
    if (!indexedDB || !IDBKeyRange) return Promise.reject(Error("Local recording storage is unavailable."));
    if (!connection) connection = new Promise((resolve, reject) => {
      const request = indexedDB.open("ndg-motion-diagnostics-v1", 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore("sessions", { keyPath: "key" });
        request.result.createObjectStore("chunks", { keyPath: ["key", "index"] });
      };
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(Error("Close the older NDG tab to open recording storage."));
      request.onsuccess = () => {
        const db = request.result;
        db.onversionchange = () => { db.close(); connection = null; };
        resolve(db);
      };
    });
    return connection;
  }
  async function transaction(mode, work) {
    const db = await database();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(["sessions", "chunks"], mode);
      let result, failure;
      tx.oncomplete = () => resolve(result);
      tx.onabort = () => reject(failure || tx.error || Error("Recording storage interrupted."));
      tx.onerror = () => {}; // onabort owns the failure
      const fail = error => { failure = error; tx.abort(); };
      try { work(tx.objectStore("sessions"), tx.objectStore("chunks"), value => { result = value; }, fail); }
      catch (error) { fail(error); }
    });
  }
  const range = key => IDBKeyRange.bound([key, 0], [key, Number.MAX_SAFE_INTEGER]);
  const update = (key, id, change) => transaction("readwrite", (sessions, chunks, done, fail) => {
    const request = sessions.get(key);
    request.onsuccess = () => {
      const session = request.result;
      if (!session || session.id !== id) return fail(Error("Recording identity changed."));
      try { change(session, chunks); sessions.put(session); done(session); }
      catch (error) { fail(error); }
    };
  });
  return {
    read: key => transaction("readonly", (sessions, chunks, done) => {
      const request = sessions.get(key); request.onsuccess = () => done(request.result || null);
    }),
    begin: (key, capture) => transaction("readwrite", (sessions, chunks, done, fail) => {
      const request = sessions.get(key);
      request.onsuccess = () => {
        if (request.result) return fail(Error("Export or delete the previous recording first."));
        const session = { ...capture, key, next_chunk: 0, saved_records: 0, state: "recording", validation_status: "unverified", teaching_eligible: false };
        sessions.add(session); done(session);
      };
    }),
    append: (key, id, index, records) => update(key, id, (session, chunks) => {
      if (session.state !== "recording" || index !== session.next_chunk) throw Error("Recording chunks are out of order.");
      chunks.add({ key, index, records });
      session.next_chunk++; session.saved_records += records.length;
    }),
    finish: (key, id, summary) => update(key, id, session => {
      // Saved chunk counts are authoritative; caller cannot overwrite them.
      session.state = summary.error ? "storage_error" : "stopped";
      session.stop_reason = summary.reason;
      session.duration_ms = summary.duration_ms;
      session.received_records = summary.received_records;
      session.error = summary.error || null;
    }),
    export: key => transaction("readonly", (sessions, chunks, done, fail) => {
      const request = sessions.get(key);
      request.onsuccess = () => {
        const session = request.result;
        if (!session) return fail(Error("No recording is stored for this lesson."));
        const records = chunks.getAll(range(key));
        records.onsuccess = () => {
          const blocks = records.result;
          if (blocks.length !== session.next_chunk || blocks.some((b, i) => b.index !== i)) return fail(Error("Recording chunks are incomplete."));
          const rows = blocks.flatMap(b => b.records);
          if (rows.length !== session.saved_records) return fail(Error("Recording count does not match storage."));
          const { key: privateKey, ...metadata } = session;
          done({ schema: "ndg-motion-recording-v1", ...metadata,
            state: session.state === "recording" ? "interrupted_snapshot" : session.state,
            validation_status: "unverified", teaching_eligible: false, records: rows });
        };
      };
    }),
    remove: key => transaction("readwrite", (sessions, chunks) => { sessions.delete(key); chunks.delete(range(key)); }),
  };
}
